import React, { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";

export interface CameraProps {
  onCapture: (image: string) => void;
  onCaptureCompleted: () => void;
}

const Camera: React.FC<CameraProps> = ({ onCapture, onCaptureCompleted }) => {
  const webcamRef = useRef<Webcam>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      onCapture(imageSrc);
      onCaptureCompleted();
    } else {
      console.warn("Capture failed: getScreenshot returned null");
    }
  }, [webcamRef, onCapture, onCaptureCompleted]);

  const toggleCamera = useCallback(() => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  }, []);

  return (
    <div className="au-camera-wrap">
      <div className="au-camera-viewport" style={{ position: "relative" }}>
        {!isReady && !error && (
          <div style={{ padding: "40px", textAlign: "center", color: "#fff" }}>
            Initializing camera...
          </div>
        )}
        {error && (
          <div style={{ padding: "40px", textAlign: "center", color: "#ff4d4f" }}>
            Camera error: {error}. Please check permissions or try a different device.
          </div>
        )}
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            facingMode: facingMode
          }}
          onUserMedia={() => setIsReady(true)}
          onUserMediaError={(e: string | DOMException) => {
            console.error("Webcam error:", e);
            setError(typeof e === "string" ? e : e.message || "Unable to access camera");
          }}
        />
        {isReady && !error && (
          <button
            onClick={toggleCamera}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "rgba(0,0,0,0.6)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "50%",
              width: "44px",
              height: "44px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              transition: "background 0.2s"
            }}
            title="Switch Camera"
            onMouseOver={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.8)"}
            onMouseOut={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.6)"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>

      <button
        className="au-submit-btn hover-trigger"
        onClick={capture}
        style={{ maxWidth: "500px" }}
        disabled={!isReady || !!error}
      >
        Capture
      </button>

      <div className="au-camera-hint">
        After the photo is taken, make sure to upload it if you need it in the report.
      </div>
    </div>
  );
};

export default Camera;
